/** Strip S3/key prefix segments; uploaded names are often `seg-seg-seg-seg-displayName`. */
export const getUploadedDocumentDisplayFileName = (
  name?: string | null,
): string => (name ?? "").split("-")[4]?.trim() || name || "";

const PDF_MIME_TYPES = new Set(["application/pdf"]);
const IMAGE_MIME_TYPES = new Set(["image/png", "image/jpeg", "image/jpg"]);
const PDF_EXTENSION = "pdf";
const IMAGE_EXTENSIONS = ["png", "jpeg", "jpg"] as const;

const decodePreviewValue = (value: string): string => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

export const hasDocumentFileExtension = (
  extensionPattern: string,
  ...values: Array<string | null | undefined>
): boolean =>
  values.some((value) => {
    if (!value) return false;
    return new RegExp(`\\.(${extensionPattern})(?:$|[?#])`, "i").test(
      decodePreviewValue(value),
    );
  });

const matchesStoredExtension = (
  extension: string,
  ...values: Array<string | null | undefined>
): boolean => {
  const suffix = extension.toLowerCase();
  return values.some((value) => {
    if (!value) return false;
    const normalized = decodePreviewValue(value).toLowerCase();
    const pathOnly = normalized.split(/[?#]/, 1)[0] ?? normalized;
    return (
      pathOnly === suffix ||
      pathOnly.endsWith(`_${suffix}`) ||
      hasDocumentFileExtension(suffix, pathOnly)
    );
  });
};

const normalizeMimeType = (mimeType?: string | null): string =>
  (mimeType ?? "").split(";", 1)[0].trim().toLowerCase();

export type DocumentPreviewIdentity = {
  name?: string | null;
  url?: string | null;
  mimeType?: string | null;
};

export const isPdfDocumentPreview = ({
  name,
  url,
  mimeType,
}: DocumentPreviewIdentity): boolean => {
  if (PDF_MIME_TYPES.has(normalizeMimeType(mimeType))) {
    return true;
  }
  return matchesStoredExtension(PDF_EXTENSION, name, url);
};

export const isImageDocumentPreview = ({
  name,
  url,
  mimeType,
}: DocumentPreviewIdentity): boolean => {
  if (IMAGE_MIME_TYPES.has(normalizeMimeType(mimeType))) {
    return true;
  }
  return IMAGE_EXTENSIONS.some((extension) =>
    matchesStoredExtension(extension, name, url),
  );
};
